import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateSwingTagsPDF } from '../utils/pdfGenerator';
import { generateSKU } from '../utils/skuGenerator';

const ItemCreation = ({ onItemCreated }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [itemData, setItemData] = useState({
    name: '',
    defaultPrice: '',
    colors: []
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Step 1: Basic Information
  const handleStep1 = (e) => {
    e.preventDefault();
    const newErrors = {};
    
    if (!itemData.name.trim()) {
      newErrors.name = 'Item name is required';
    }
    if (!itemData.defaultPrice || itemData.defaultPrice <= 0) {
      newErrors.defaultPrice = 'Valid price is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setCurrentStep(2);
  };

  // Step 2: Colors and Sizes
  const addColor = () => {
    setItemData({
      ...itemData,
      colors: [...itemData.colors, { name: '', sizes: [] }]
    });
  };

  const updateColor = (index, value) => {
    const newColors = [...itemData.colors];
    newColors[index].name = value;
    setItemData({ ...itemData, colors: newColors });
  };

  const removeColor = (index) => {
    const newColors = itemData.colors.filter((_, i) => i !== index);
    setItemData({ ...itemData, colors: newColors });
  };

  const addSize = (colorIndex) => {
    const newColors = [...itemData.colors];
    newColors[colorIndex].sizes.push({
      name: '',
      quantity: 1,
      price: itemData.defaultPrice
    });
    setItemData({ ...itemData, colors: newColors });
  };

  const updateSize = (colorIndex, sizeIndex, field, value) => {
    const newColors = [...itemData.colors];
    newColors[colorIndex].sizes[sizeIndex][field] = value;
    setItemData({ ...itemData, colors: newColors });
  };

  const removeSize = (colorIndex, sizeIndex) => {
    const newColors = [...itemData.colors];
    newColors[colorIndex].sizes = newColors[colorIndex].sizes.filter((_, i) => i !== sizeIndex);
    setItemData({ ...itemData, colors: newColors });
  };

  const handleStep2 = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (itemData.colors.length === 0) {
      newErrors.colors = 'At least one color is required';
    }

    itemData.colors.forEach((color, ci) => {
      if (!color.name.trim()) {
        newErrors[`color_${ci}`] = 'Color name is required';
      }
      if (color.sizes.length === 0) {
        newErrors[`color_${ci}_sizes`] = 'At least one size required for each color';
      }
      color.sizes.forEach((size, si) => {
        if (!size.name.trim()) {
          newErrors[`size_${ci}_${si}`] = 'Size name is required';
        }
      });
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setCurrentStep(3);
  };

  // Step 3: Confirmation
  const getTotalVariations = () => {
    return itemData.colors.reduce((total, color) => {
      return total + color.sizes.reduce((sum, size) => sum + parseInt(size.quantity), 0);
    }, 0);
  };

  const getTotalValue = () => {
    return itemData.colors.reduce((total, color) => {
      return total + color.sizes.reduce((sum, size) => {
        return sum + (parseFloat(size.price) * parseInt(size.quantity));
      }, 0);
    }, 0);
  };

  // Step 4: Save
  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Generate SKU
      const sku = generateSKU(itemData.name);

      // Prepare variations for Square API
      const variations = [];
      itemData.colors.forEach(color => {
        color.sizes.forEach(size => {
          variations.push({
            color: color.name,
            size: size.name,
            quantity: parseInt(size.quantity),
            price: parseFloat(size.price),
            sku: `${sku}-${color.name.substring(0, 3).toUpperCase()}-${size.name.toUpperCase()}`
          });
        });
      });

      const item = {
        name: itemData.name,
        defaultPrice: parseFloat(itemData.defaultPrice),
        sku: sku,
        variations: variations,
        createdAt: new Date().toISOString()
      };

      // Save to Square API
      const savedItem = await onItemCreated(item);

      // Generate swing tags PDF
      await generateSwingTagsPDF(savedItem);

      // Success notification
      alert('Item created successfully! Swing tags PDF has been downloaded.');
      
      // Navigate to items list
      navigate('/');
    } catch (error) {
      console.error('Error creating item:', error);
      alert('Error creating item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${currentStep >= step ? 'text-blue-600' : 'text-gray-500'}`}>
                    {step === 1 && 'Basic Info'}
                    {step === 2 && 'Colors & Sizes'}
                    {step === 3 && 'Confirmation'}
                  </p>
                </div>
              </div>
              {step < 3 && (
                <div
                  className={`flex-1 h-1 mx-4 ${
                    currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <form onSubmit={handleStep1}>
            <h2 className="text-2xl font-bold mb-6">Basic Information</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Item Name *
              </label>
              <input
                type="text"
                value={itemData.name}
                onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Cotton T-Shirt"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Price ($) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={itemData.defaultPrice}
                onChange={(e) => setItemData({ ...itemData, defaultPrice: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.defaultPrice ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="29.99"
              />
              {errors.defaultPrice && (
                <p className="mt-1 text-sm text-red-600">{errors.defaultPrice}</p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Next
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Colors and Sizes */}
        {currentStep === 2 && (
          <form onSubmit={handleStep2}>
            <h2 className="text-2xl font-bold mb-6">Colors & Sizes</h2>

            {errors.colors && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.colors}</p>
              </div>
            )}

            <div className="space-y-6 mb-6">
              {itemData.colors.map((color, colorIndex) => (
                <div key={colorIndex} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="text"
                      value={color.name}
                      onChange={(e) => updateColor(colorIndex, e.target.value)}
                      className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors[`color_${colorIndex}`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Color name (e.g., Navy Blue)"
                    />
                    <button
                      type="button"
                      onClick={() => removeColor(colorIndex)}
                      className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                    >
                      Remove Color
                    </button>
                  </div>

                  {errors[`color_${colorIndex}_sizes`] && (
                    <p className="text-sm text-red-600 mb-2">{errors[`color_${colorIndex}_sizes`]}</p>
                  )}

                  {/* Sizes */}
                  <div className="ml-4 space-y-2">
                    {color.sizes.map((size, sizeIndex) => (
                      <div key={sizeIndex} className="flex items-center gap-3">
                        <input
                          type="text"
                          value={size.name}
                          onChange={(e) => updateSize(colorIndex, sizeIndex, 'name', e.target.value)}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Size"
                        />
                        <input
                          type="number"
                          min="1"
                          value={size.quantity}
                          onChange={(e) => updateSize(colorIndex, sizeIndex, 'quantity', e.target.value)}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Qty"
                        />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={size.price}
                          onChange={(e) => updateSize(colorIndex, sizeIndex, 'price', e.target.value)}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Price"
                        />
                        <button
                          type="button"
                          onClick={() => removeSize(colorIndex, sizeIndex)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addSize(colorIndex)}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      + Add Size
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addColor}
              className="w-full mb-6 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600"
            >
              + Add Color
            </button>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Confirmation */}
        {currentStep === 3 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Confirmation</h2>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">{itemData.name}</h3>
                <p className="text-gray-600">Default Price: ${itemData.defaultPrice}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Variations</h4>
                {itemData.colors.map((color, ci) => (
                  <div key={ci} className="mb-4 last:mb-0">
                    <p className="font-medium text-sm text-gray-700 mb-2">{color.name}</p>
                    <div className="ml-4 space-y-1">
                      {color.sizes.map((size, si) => (
                        <div key={si} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Size {size.name} × {size.quantity}
                          </span>
                          <span className="font-medium">${size.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-700">Total Variations:</span>
                  <span className="font-semibold">{getTotalVariations()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Total Inventory Value:</span>
                  <span className="font-semibold text-blue-600">${getTotalValue().toFixed(2)}</span>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> After saving, swing tags will be automatically generated and downloaded as a PDF.
                </p>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                disabled={saving}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Item & Generate Tags'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemCreation;
